/**
 * VideoPlayer is engine-agnostic: it never imports `expo-video` (or any other
 * playback library) and doesn't add it as a dependency of `a3s-ui`.
 *
 * `a3s-ui` has almost no runtime dependencies today, and the one existing
 * component that needs a genuinely platform-native capability —
 * `MediaCarousel`, which routinely renders video/image content — follows the
 * same shape: it never imports `expo-image`/`expo-video` itself and instead
 * takes a `renderMedia` render prop, leaving the actual media element to the
 * consumer. `VideoPlayer` mirrors that with `renderVideoSurface`. This hook
 * therefore owns only generic chrome state (hover/tap-to-reveal controls, the
 * fade animation, box sizing) — playback state (`playing`, `currentTime`,
 * `muted`, …) and the actions that mutate it are supplied by the consumer,
 * which owns the actual player instance.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Platform, type View, type ViewStyle } from "react-native";
import {
  Easing,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type AnimatedStyle,
} from "react-native-reanimated";
import { useLatestRef } from "../../hooks/use-latest-ref";

const FADE_DURATION_MS = 150;
const AUTO_HIDE_DELAY_MS = 3000;
const ASPECT_RATIO_16_9 = 16 / 9;

export interface UseVideoPlayerOptions {
  readonly fullscreen: boolean;
  /** Reaching the end of playback reveals controls (so the restart affordance is visible) even if the user hasn't tapped/hovered. */
  readonly ended: boolean;
  /**
   * Changing this value forces the tap/hover-revealed controls back to hidden.
   * The value is never interpreted — only its identity is compared — so a
   * consumer can pass whatever identifies "this is a different thing now"
   * (e.g. the current media id, or that id combined with a paused flag).
   */
  readonly resetControlsKey: unknown;
  readonly boxHeight: number | undefined;
  readonly fillParent: boolean;
  readonly maxHeight: number | undefined;
  readonly onPlayPause: (() => void) | undefined;
  readonly onMuteToggle: (() => void) | undefined;
  readonly onFullscreenToggle: (() => void) | undefined;
  readonly onPrevious: (() => void) | undefined;
  readonly onNext: (() => void) | undefined;
  readonly onScrubStart: (() => void) | undefined;
  readonly onScrubEnd: (() => void) | undefined;
}

export interface UseVideoPlayerReturn {
  readonly controlsVisible: boolean;
  readonly controlsAnimatedStyle: AnimatedStyle<ViewStyle>;
  readonly boxStyle: ViewStyle;
  /** Props to spread on the Pressable that wraps the video surface. */
  readonly surfaceHandlers: {
    readonly onPress: () => void;
  };
  /** Attach to the outermost player box — used on web to track hover presence. No-op ref on native. */
  readonly boxRef: RefObject<View | null>;
  readonly handlePlayPause: () => void;
  readonly handleMuteToggle: () => void;
  readonly handleFullscreenToggle: () => void;
  readonly handlePrevious: () => void;
  readonly handleNext: () => void;
  readonly handleScrubStart: () => void;
  readonly handleScrubEnd: () => void;
}

function resolveBoxStyle(options: {
  readonly fullscreen: boolean;
  readonly fillParent: boolean;
  readonly boxHeight: number | undefined;
  readonly maxHeight: number | undefined;
}): ViewStyle {
  const { fullscreen, fillParent, boxHeight, maxHeight } = options;

  // Fullscreen (native chrome or an equivalent immersive context) always wins:
  // the OS/native player drives the actual on-screen size, so the in-page box
  // only needs *a* height to avoid collapsing to 0 — an aspect ratio gives it
  // one without fighting the native chrome.
  if (fullscreen) return { aspectRatio: ASPECT_RATIO_16_9 };
  if (fillParent) return { height: "100%", flex: 1 };
  if (boxHeight !== undefined) return { height: boxHeight };
  if (maxHeight !== undefined) {
    return { aspectRatio: ASPECT_RATIO_16_9, maxHeight, maxWidth: maxHeight * ASPECT_RATIO_16_9 };
  }
  return { aspectRatio: ASPECT_RATIO_16_9 };
}

export function useVideoPlayer(options: UseVideoPlayerOptions): UseVideoPlayerReturn {
  const {
    fullscreen,
    ended,
    resetControlsKey,
    boxHeight,
    fillParent,
    maxHeight,
    onPlayPause,
    onMuteToggle,
    onFullscreenToggle,
    onPrevious,
    onNext,
    onScrubStart,
    onScrubEnd,
  } = options;

  const [hoverVisible, setHoverVisible] = useState(false);
  const [tapVisible, setTapVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<View>(null);
  // Tracks actual pointer presence independent of `hoverVisible` state, so a
  // forced reset (see `resetControlsKey` below) can tell whether the cursor
  // is still over the box. `mouseenter`/`mouseleave` only fire on a genuine
  // boundary crossing — resetting `hoverVisible` doesn't move the cursor, so
  // no new `mouseenter` follows a reset, and without this ref hover-to-reveal
  // would stay dead until the cursor actually leaves and re-enters.
  const isPointerInsideRef = useRef(false);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Web-only hover tracking via raw `mouseenter`/`mouseleave` listeners on
  // the box, rather than Pressable's onHoverIn/onHoverOut (pointerenter/
  // pointerleave on whichever element is currently hit-tested). Revealing a
  // chrome button flips its `pointer-events` from `none` to `auto` right
  // under the cursor; Chromium then re-runs hit-testing and retargets to
  // that newly-hittable descendant, which spuriously re-fires both leave AND
  // enter on the box even though the cursor never moved — confirmed by
  // instrumentation: every micro-jitter produced a fresh enter+leave pair, a
  // self-sustaining flicker loop, not just noise. The `mouseleave` handler
  // below defends against that by checking the event's own coordinates
  // against the box's current bounding rect before hiding: a spurious leave
  // fired mid-retarget still reports coordinates *inside* the box (the
  // cursor didn't actually move), so it's ignored; a genuine leave reports
  // coordinates outside and hides normally. This is deterministic — no
  // debounce/timeout window needed, and a cursor resting motionless over the
  // player (no further events at all) correctly stays revealed indefinitely.
  // Native has no hover concept (touch never fires this), so the effect is a
  // no-op there.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    // RNW forwards `ref` on View/Pressable to the underlying DOM node. Widen
    // to `unknown` and narrow with `instanceof` rather than asserting through
    // it: the guard is what actually proves the node is an element, and it
    // keeps the hook free of `as` casts (docs/principles.md, Code conventions).
    const candidate: unknown = boxRef.current;
    if (!(candidate instanceof HTMLElement)) return;
    const node = candidate;

    const handleEnter = () => {
      isPointerInsideRef.current = true;
      setHoverVisible(true);
    };
    const handleLeave = (event: MouseEvent) => {
      const rect = node.getBoundingClientRect();
      const stillInside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!stillInside) {
        isPointerInsideRef.current = false;
        setHoverVisible(false);
      }
    };

    node.addEventListener("mouseenter", handleEnter);
    node.addEventListener("mouseleave", handleLeave);
    return () => {
      node.removeEventListener("mouseenter", handleEnter);
      node.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  // Reset the tap/hover-driven visibility whenever `fullscreen` turns off, so
  // a state left over from before entering fullscreen doesn't leak into the
  // page view. This adjusts state during render in response to a prop change
  // (the React-endorsed alternative to a `useEffect` that only mirrors a
  // prop) rather than a side effect — resetting a couple of booleans and
  // clearing a timer ref is idempotent and has no external system to
  // synchronise with.
  const [prevFullscreen, setPrevFullscreen] = useState(fullscreen);
  if (fullscreen !== prevFullscreen) {
    setPrevFullscreen(fullscreen);
    if (!fullscreen) {
      setTapVisible(false);
      setHoverVisible(false);
      clearHideTimeout();
    }
  }

  // Reaching the end of playback reveals controls unconditionally (so the
  // restart affordance is visible without a tap) and does not arm the
  // auto-hide countdown — mirrors the original clips player, which showed
  // controls indefinitely once a clip ended.
  const [prevEnded, setPrevEnded] = useState(ended);
  if (ended !== prevEnded) {
    setPrevEnded(ended);
    if (ended) {
      setTapVisible(true);
      clearHideTimeout();
    }
  }

  // Chrome visibility is per-media state: it lives in this hook, so when the
  // consumer swaps in a different clip (or parks the player — a carousel slide
  // going inactive), leftover revealed controls would otherwise carry over into
  // the next thing shown. A changing `resetControlsKey` forces the *tap*-
  // revealed state hidden unconditionally — but hover is re-synced to
  // `isPointerInsideRef` rather than forced false: the cursor hasn't moved
  // just because the key changed, so if it's still over the box, hover-reveal
  // should keep working (matching every other video player's behaviour —
  // controls stay visible while the mouse rests on it). Forcing hoverVisible
  // false here would leave hover-to-reveal dead until an actual leave+re-enter,
  // since no new `mouseenter` follows a reset. Same render-time state-diff
  // idiom as `fullscreen`/`ended` above.
  const [prevResetControlsKey, setPrevResetControlsKey] = useState(resetControlsKey);
  if (!Object.is(resetControlsKey, prevResetControlsKey)) {
    setPrevResetControlsKey(resetControlsKey);
    setTapVisible(false);
    setHoverVisible(isPointerInsideRef.current);
    clearHideTimeout();
  }

  const controlsVisible = fullscreen || hoverVisible || tapVisible;
  const fullscreenRef = useLatestRef(fullscreen);

  const armAutoHide = useCallback(() => {
    clearHideTimeout();
    if (fullscreenRef.current) return;
    hideTimeoutRef.current = setTimeout(() => {
      setTapVisible(false);
      hideTimeoutRef.current = null;
    }, AUTO_HIDE_DELAY_MS);
  }, [clearHideTimeout, fullscreenRef]);

  /** Show controls and (re)start the auto-hide countdown — used by actions the user should see reflected in the chrome (e.g. flipping the mute icon). */
  const keepVisible = useCallback(() => {
    setTapVisible(true);
    armAutoHide();
  }, [armAutoHide]);

  const handleSurfacePress = useCallback(() => {
    clearHideTimeout();
    setTapVisible((current) => {
      const next = !current;
      if (next && !fullscreenRef.current) armAutoHide();
      return next;
    });
  }, [armAutoHide, clearHideTimeout, fullscreenRef]);

  // --- Fade animation ---------------------------------------------------
  const controlsOpacity = useSharedValue(controlsVisible ? 1 : 0);
  useAnimatedReaction(
    () => controlsVisible,
    (shouldShow) => {
      controlsOpacity.value = withTiming(shouldShow ? 1 : 0, {
        duration: FADE_DURATION_MS,
        easing: Easing.out(Easing.ease),
      });
    },
    [controlsVisible],
  );

  const controlsAnimatedStyle = useAnimatedStyle(() => ({
    opacity: controlsOpacity.value,
    // Gate interactivity on the *actual* animated opacity, not the React
    // `controlsVisible` state. On web the fade opacity (reanimated) and a
    // `pointerEvents` prop (React state) can desync, leaving a faded-out
    // control that still captures clicks. Tying pointer-events to opacity
    // guarantees a fully transparent control is never clickable. "auto"/"none"
    // (rather than "box-none") because reanimated writes this via the style
    // path on web, where "box-none" is not valid CSS. This style is meant for
    // button-sized controls — NOT the full-cover overlay layer, which stays
    // hit-testable via a static `pointerEvents="box-none"` JSX prop so empty
    // area taps fall through to the surface's tap-to-toggle.
    pointerEvents: controlsOpacity.value > 0 ? "auto" : "none",
  }));

  // --- Action wrappers ----------------------------------------------------
  const onPlayPauseRef = useLatestRef(onPlayPause);
  const onMuteToggleRef = useLatestRef(onMuteToggle);
  const onFullscreenToggleRef = useLatestRef(onFullscreenToggle);
  const onPreviousRef = useLatestRef(onPrevious);
  const onNextRef = useLatestRef(onNext);
  const onScrubStartRef = useLatestRef(onScrubStart);
  const onScrubEndRef = useLatestRef(onScrubEnd);

  const handlePlayPause = useCallback(() => {
    onPlayPauseRef.current?.();
  }, [onPlayPauseRef]);

  const handleMuteToggle = useCallback(() => {
    onMuteToggleRef.current?.();
    // Keep the controls visible long enough to see the icon flip — skipped in
    // fullscreen, which has its own chrome and doesn't auto-hide anyway.
    keepVisible();
  }, [keepVisible, onMuteToggleRef]);

  const handleFullscreenToggle = useCallback(() => {
    onFullscreenToggleRef.current?.();
  }, [onFullscreenToggleRef]);

  const handlePrevious = useCallback(() => {
    onPreviousRef.current?.();
  }, [onPreviousRef]);

  const handleNext = useCallback(() => {
    onNextRef.current?.();
  }, [onNextRef]);

  const handleScrubStart = useCallback(() => {
    clearHideTimeout();
    setTapVisible(true);
    onScrubStartRef.current?.();
  }, [clearHideTimeout, onScrubStartRef]);

  const handleScrubEnd = useCallback(() => {
    armAutoHide();
    onScrubEndRef.current?.();
  }, [armAutoHide, onScrubEndRef]);

  const boxStyle = useMemo(
    () => resolveBoxStyle({ fullscreen, fillParent, boxHeight, maxHeight }),
    [fullscreen, fillParent, boxHeight, maxHeight],
  );

  return {
    controlsVisible,
    controlsAnimatedStyle,
    boxStyle,
    surfaceHandlers: {
      onPress: handleSurfacePress,
    },
    boxRef,
    handlePlayPause,
    handleMuteToggle,
    handleFullscreenToggle,
    handlePrevious,
    handleNext,
    handleScrubStart,
    handleScrubEnd,
  };
}
